import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { chatManager } from "../../data/managers/ChatManager";
import { ChatModel } from "../../data/models/Chat/ChatModel";
import {
  MessageModel,
  MessageTypeEnum,
} from "../../data/models/Chat/MessageModel";
import { TypeRoleEnum } from "../../data/models/Chat/TypeRoleEnum";
import {
  useAllChats,
  useFirestoreMessages,
} from "../../data/providers/FirestoreProvider";
import { useSoulAccess } from "../../data/providers/SoulProvider";
import { useStorageImages } from "../../data/providers/StorageProvider";
import { useDebugMessagesPreference } from "./useDebugMessagesPreference";

export function useChats() {
  const [selectedChat, setSelectedChat] = useState<ChatModel | undefined>();
  const [selectedMessages, setSelectedMessages] = useState<
    MessageModel[] | undefined
  >();
  const [, setOptimisticMessages] = useState<MessageModel[]>([]);
  const [newMessage, setNewMessage] = useState<string>("");
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 900);
  const [composerNotice, setComposerNotice] = useState<{
    severity: "warning" | "error";
    message: string;
  } | null>(null);
  const endOfMessagesRef = useRef<HTMLDivElement>(null);
  const { uploadFileAndGetUrl } = useStorageImages();
  const { soul } = useSoulAccess();
  const company = soul?.company;
  const {
    showDebugMessages,
    setShowDebugMessages,
    toggleDebugMessages,
  } = useDebugMessagesPreference();

  const {
    chats: allChats,
    loading: loadingChats,
    error: errorChats,
    updateBotStatus,
  } = useAllChats(company!);
  const liveSelectedChat =
    allChats.find((chat) => chat.phoneNumber === selectedChat?.phoneNumber) ||
    selectedChat;

  const {
    messages,
    loading: loadingMessages,
    error: errorMessages,
  } = useFirestoreMessages(
    company!,
    liveSelectedChat?.phoneNumber!,
    liveSelectedChat?.serviceActive
  );

  const parseMessageMetadata = (value: MessageModel["contentMetadata"]) => {
    if (!value) return null;
    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch (_error) {
        return null;
      }
    }
    return value;
  };

  const getAudioFilename = (mimeType?: string) => {
    const normalizedMimeType = String(mimeType || "").toLowerCase();
    if (normalizedMimeType.includes("ogg")) {
      return `voice-${Date.now()}.ogg`;
    }
    if (normalizedMimeType.includes("mpeg") || normalizedMimeType.includes("mp3")) {
      return `voice-${Date.now()}.mp3`;
    }
    if (normalizedMimeType.includes("wav")) {
      return `voice-${Date.now()}.wav`;
    }
    return `voice-${Date.now()}.webm`;
  };

  const isOptimisticMessageConfirmed = useCallback(
    (optimisticMessage: MessageModel, persistedMessage: MessageModel) => {
      if (optimisticMessage.id && persistedMessage.id) {
        return optimisticMessage.id === persistedMessage.id;
      }

      if (
        optimisticMessage.role !== persistedMessage.role ||
        optimisticMessage.messageType !== persistedMessage.messageType
      ) {
        return false;
      }

      if (optimisticMessage.messageType === MessageTypeEnum.TEXT) {
        return optimisticMessage.content === persistedMessage.content;
      }

      const optimisticMetadata = parseMessageMetadata(
        optimisticMessage.contentMetadata
      );
      const persistedMetadata = parseMessageMetadata(
        persistedMessage.contentMetadata
      );

      if (!optimisticMetadata || !persistedMetadata) {
        return false;
      }

      const sameMimeType =
        optimisticMetadata.mimeType &&
        persistedMetadata.mimeType &&
        optimisticMetadata.mimeType === persistedMetadata.mimeType;
      const sameSize =
        optimisticMetadata.size &&
        persistedMetadata.size &&
        optimisticMetadata.size === persistedMetadata.size;
      const sameFileName =
        optimisticMetadata.fileName &&
        persistedMetadata.fileName &&
        optimisticMetadata.fileName === persistedMetadata.fileName;

      if ((sameMimeType && sameSize) || sameFileName) {
        return true;
      }

      const optimisticTimestamp = new Date(
        optimisticMessage.sourceCreatedAt ||
          optimisticMessage.createdAt
      ).getTime();
      const persistedTimestamp = new Date(
        persistedMessage.sourceCreatedAt ||
          persistedMessage.createdAt
      ).getTime();

      return (
        persistedTimestamp >= optimisticTimestamp &&
        persistedTimestamp - optimisticTimestamp <= 45000
      );
    },
    []
  );

  useEffect(() => {
    if (!selectedChat) {
      setSelectedMessages(undefined);
      setOptimisticMessages([]);
      return;
    }

    const persistedMessages = messages || [];
    setOptimisticMessages((currentOptimisticMessages) => {
      const unresolvedOptimisticMessages = currentOptimisticMessages.filter(
        (optimisticMessage) =>
          optimisticMessage.status === "failed" ||
          !persistedMessages.some((persistedMessage) =>
            isOptimisticMessageConfirmed(optimisticMessage, persistedMessage)
          )
      );

      setSelectedMessages([
        ...persistedMessages,
        ...unresolvedOptimisticMessages,
      ]);

      return unresolvedOptimisticMessages;
    });
  }, [isOptimisticMessageConfirmed, messages, selectedChat]);

  useEffect(() => {
    if (!selectedChat?.phoneNumber) {
      return;
    }

    const refreshedSelectedChat = allChats.find(
      (chat) => chat.phoneNumber === selectedChat.phoneNumber
    );
    if (!refreshedSelectedChat || refreshedSelectedChat === selectedChat) {
      return;
    }

    setSelectedChat(refreshedSelectedChat);
  }, [allChats, selectedChat]);

  useEffect(() => {
    const handleResize = () => {
      const isNowMobile = window.innerWidth < 900;
      setIsMobile(isNowMobile);
      if (!isNowMobile && selectedChat === null) {
        setSelectedChat(allChats?.[0]);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [allChats, selectedChat]);

  const scrollToBottom = useCallback(() => {
    const container = endOfMessagesRef.current?.parentElement;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  const getEffectiveMessageDate = useCallback(
    (message: MessageModel) => message.sourceCreatedAt || message.createdAt,
    []
  );

  async function handleSelectChat(chat: ChatModel) {
    setSelectedChat((current) =>
      current?.phoneNumber === chat.phoneNumber ? undefined : chat
    );
  }

  // function handleSendMessage() {
  //   if (newMessage.trim() && selectedChat) {
  //     setSelectedChat({
  //       ...selectedChat,
  //       messages: [...selectedChat.messages, newMessage],
  //     });
  //     setNewMessage("");
  //   }
  // }

  function handleBack() {
    setSelectedChat(undefined);
    setSelectedMessages(undefined);
    setOptimisticMessages([]);
  }

  const groupMessagesByDate = (messages: MessageModel[]) => {
    return messages.reduce((groups: any, message: any) => {
      const date = new Date(
        getEffectiveMessageDate(message)
      ).toLocaleDateString("sv-SE");
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
      return groups;
    }, {});
  };

  const formatDate = (dateString: string) => {
    const parsedDate = new Date(`${dateString}T00:00:00`);
    if (isNaN(parsedDate.getTime())) {
      return "Fecha no válida";
    }

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    if (parsedDate.toDateString() === today.toDateString()) {
      return "Hoy";
    } else if (parsedDate.toDateString() === yesterday.toDateString()) {
      return "Ayer";
    }

    return new Intl.DateTimeFormat("es-ES", { dateStyle: "medium" }).format(
      parsedDate
    );
  };

  const sendMessage = async (
    phoneNumber: string,
    options:
      | { type: MessageTypeEnum.TEXT; content: string }
      | { type: MessageTypeEnum.IMAGE; file: File }
      | { type: MessageTypeEnum.VIDEO; file: File }
      | { type: MessageTypeEnum.DOCUMENT; file: File }
      | { type: MessageTypeEnum.AUDIO; blob: Blob },
    replyContext?: {
      replyToMessageId?: string;
      replyToSender?: string;
      replyToRole?: string;
      replyPreview?: string;
    }
  ) => {
    let content = "";
    let metadata: any = undefined;

    if (options.type === MessageTypeEnum.TEXT) {
      content = options.content;
    }

    if (options.type === MessageTypeEnum.IMAGE) {
      content = URL.createObjectURL(options.file);
      metadata = {
        fileName: options.file.name,
        size: options.file.size,
        mimeType: options.file.type,
      };
    }

    if (options.type === MessageTypeEnum.VIDEO) {
      content = URL.createObjectURL(options.file);
      metadata = {
        fileName: options.file.name,
        size: options.file.size,
        mimeType: options.file.type,
      };
    }

    if (options.type === MessageTypeEnum.DOCUMENT) {
      content = URL.createObjectURL(options.file);
      metadata = {
        fileName: options.file.name,
        size: options.file.size,
        mimeType: options.file.type,
      };
    }

    if (options.type === MessageTypeEnum.AUDIO) {
      content = URL.createObjectURL(options.blob);
      metadata = {
        fileName: getAudioFilename(options.blob.type),
        size: options.blob.size,
        mimeType: options.blob.type || "audio/webm",
      };
    }

    if (replyContext?.replyToMessageId) {
      metadata = {
        ...(metadata || {}),
        replyToMessageId: replyContext.replyToMessageId,
        replyToSender: replyContext.replyToSender,
        replyToRole: replyContext.replyToRole,
        replyPreview: replyContext.replyPreview,
      };
    }

    // Mensaje temporal (con URL local si aplica)
    const tempMessage = new MessageModel({
      id: uuidv4(),
      content,
      role: TypeRoleEnum.ASSISTANT,
      createdAt: new Date(),
      messageType: options.type,
      status: "pending",
      contentMetadata: metadata ? JSON.stringify(metadata) : undefined,
    });

    setOptimisticMessages((prev) => [...prev, tempMessage]);
    setSelectedMessages((prev) => [...(prev || []), tempMessage]);

    if (options.type === MessageTypeEnum.TEXT) {
      content = options.content;
    }

    if (options.type === MessageTypeEnum.IMAGE) {
      content = await uploadFileAndGetUrl(options.file, phoneNumber, "image");
      metadata = {
        fileName: options.file.name,
        size: options.file.size,
        mimeType: options.file.type,
      };
    }

    if (options.type === MessageTypeEnum.VIDEO) {
      content = await uploadFileAndGetUrl(options.file, phoneNumber, "video");
      metadata = {
        fileName: options.file.name,
        size: options.file.size,
        mimeType: options.file.type,
      };
    }

    if (options.type === MessageTypeEnum.DOCUMENT) {
      content = await uploadFileAndGetUrl(options.file, phoneNumber, "document");
      metadata = {
        fileName: options.file.name,
        size: options.file.size,
        mimeType: options.file.type,
      };
    }

    if (options.type === MessageTypeEnum.AUDIO) {
      const audioFile = new File([options.blob], getAudioFilename(options.blob.type), {
        type: options.blob.type || "audio/webm",
      });

      content = await uploadFileAndGetUrl(audioFile, phoneNumber, "audio");
      metadata = {
        fileName: audioFile.name,
        size: audioFile.size,
        mimeType: audioFile.type,
      };
    }

    let failedSendDetails: {
      requiresTemplate?: boolean;
      metaCode?: number | string;
    } = {};
    try {
      const result = await chatManager.sendMessage({
        phoneNumber,
        type: options.type,
        message: content,
        clientMessageId: tempMessage.id,
        replyToMessageId: replyContext?.replyToMessageId,
        replyToSender: replyContext?.replyToSender,
        replyToRole: replyContext?.replyToRole,
        replyPreview: replyContext?.replyPreview,
      });

      if (!result.sent) {
        failedSendDetails = {
          requiresTemplate: result.requiresTemplate,
          metaCode: result.metaCode,
        };
        if (result.requiresTemplate) {
          setComposerNotice({
            severity: "warning",
            message:
              result.errorMessage ||
              (result.conversationWindowOpen === false
                ? "La ventana de 24 horas no está abierta. Debes enviar una plantilla desde la configuración de WhatsApp."
                : "WhatsApp exige una plantilla para este envío."),
          });
          setComposerNotice({
            severity: "warning",
            message:
              "No se envio el mensaje. WhatsApp no permite iniciar una conversacion con mensajes normales: debes esperar a que el cliente te escriba o responda. Mientras tanto, solo puedes contactarlo mediante una plantilla aprobada. Abre el boton Plantillas junto al clip para enviarla.",
          });
        } else {
          setComposerNotice({
            severity: "error",
            message:
              result.errorMessage
                ? `No se pudo enviar el mensaje: ${result.errorMessage}`
                : "No se pudo enviar el mensaje. Revisa la conexion y confirma que el cliente tenga una conversacion abierta o utiliza una plantilla aprobada.",
          });
        }
        throw new Error(result.errorMessage || "No se pudo enviar el mensaje.");
      }
      setComposerNotice(null);
    } catch (err) {
      const failureReason = failedSendDetails.requiresTemplate
        ? "No se envio el mensaje. WhatsApp no permite mensajes normales hasta que el cliente te escriba o responda. Mientras tanto, debes usar una plantilla aprobada para iniciar o reabrir la conversacion."
        : err instanceof Error && err.message
        ? err.message
        : "WhatsApp no pudo entregar este mensaje.";
      const markFailed = (message: MessageModel) => {
        if (message.id !== tempMessage.id) return message;
        let metadata: Record<string, unknown> = {};
        try {
          metadata =
            typeof message.contentMetadata === "string"
              ? JSON.parse(message.contentMetadata)
              : message.contentMetadata || {};
        } catch {
          metadata = {};
        }
        return {
          ...message,
          status: "failed" as const,
          contentMetadata: JSON.stringify({
            ...metadata,
            failureReason,
            failureRequiresTemplate: failedSendDetails.requiresTemplate || false,
            failureMetaCode: failedSendDetails.metaCode,
            failureRetryable: !failedSendDetails.requiresTemplate,
          }),
        };
      };
      setOptimisticMessages((prev) =>
        (prev || []).map(markFailed)
      );
      setSelectedMessages((prev) =>
        (prev || []).map(markFailed)
      );
    }
  };

  return {
    selectedChat,
    setSelectedChat,
    selectedMessages,
    setSelectedMessages,
    newMessage,
    setNewMessage,
    isMobile,
    setIsMobile,
    endOfMessagesRef,
    userData: soul,
    company,
    allChats,
    loadingChats,
    errorChats,
    messages,
    loadingMessages,
    errorMessages,
    handleSelectChat,
    // handleSendMessage, // descomenta si lo usas
    handleBack,
    scrollToBottom,
    groupMessagesByDate,
    getEffectiveMessageDate,
    formatDate,
    sendMessage,
    composerNotice,
    setComposerNotice,
    updateBotStatus,
    showDebugMessages,
    setShowDebugMessages,
    toggleDebugMessages,
  };
}
